import type {
  Agent,
  Country,
  Dataset,
  FeatureDef,
  ShippingLine,
  ShippingPrice,
  Source,
  ScoringConfig,
} from "@/types";

import agentsRaw from "@data/agents.json";
import linesRaw from "@data/shipping_lines.json";
import pricesRaw from "@data/shipping_prices.json";
import countriesRaw from "@data/countries.json";
import featuresRaw from "@data/features.json";
import sourcesRaw from "@data/sources.json";
import scoringRaw from "@data/scoring_config.json";
import refQuotesRaw from "@data/reference_quotes.json";

// JSON imports are validated at build time by scripts/validate.ts. Here we cast
// to the domain types; the loader also builds lookup indexes used across the UI.
export const dataset: Dataset = {
  agents: agentsRaw as unknown as Agent[],
  shippingLines: linesRaw as unknown as ShippingLine[],
  shippingPrices: pricesRaw as unknown as ShippingPrice[],
  countries: countriesRaw as unknown as Country[],
  features: featuresRaw as unknown as FeatureDef[],
  sources: sourcesRaw as unknown as Record<string, Source>,
  scoring: scoringRaw as unknown as ScoringConfig,
  referenceQuotes: refQuotesRaw as unknown as Dataset["referenceQuotes"],
  meta: {
    generatedAt: "2026-07-23",
    schemaVersion: "1.0.0",
  },
};

export const referenceQuotesByAgent = new Map<string, typeof dataset.referenceQuotes.quotes>();
for (const q of dataset.referenceQuotes.quotes) {
  const arr = referenceQuotesByAgent.get(q.agentId) ?? [];
  arr.push(q);
  referenceQuotesByAgent.set(q.agentId, arr);
}

// ---- Indexes ---------------------------------------------------------------

export const agentById = new Map(dataset.agents.map((a) => [a.id, a]));
export const lineById = new Map(dataset.shippingLines.map((l) => [l.id, l]));
export const countryByCode = new Map(dataset.countries.map((c) => [c.code, c]));
export const featureById = new Map(dataset.features.map((f) => [f.id, f]));

export const linesByAgent = new Map<string, ShippingLine[]>();
for (const line of dataset.shippingLines) {
  const arr = linesByAgent.get(line.agentId) ?? [];
  arr.push(line);
  linesByAgent.set(line.agentId, arr);
}

export const pricesByLine = new Map<string, ShippingPrice[]>();
for (const p of dataset.shippingPrices) {
  const arr = pricesByLine.get(p.lineId) ?? [];
  arr.push(p);
  pricesByLine.set(p.lineId, arr);
}

// ---- Live quotes (100% real, captured from each agent's own calculator) ----
// The comparison surfaces run ONLY on these — no modeled/seed prices are shown.
export type LiveQuote = typeof dataset.referenceQuotes.quotes[number];

export const liveParcelSpecId = "owncalc-5kg";
export const liveParcelSpec = dataset.referenceQuotes.parcelSpecs[liveParcelSpecId];
// Floor of ¥10: a real 5 kg international parcel is never cheaper; this drops
// placeholder "price-inquiry" / coupon-teaser rows (e.g. ¥1) that aren't real quotes.
export const liveQuotes: LiveQuote[] = dataset.referenceQuotes.quotes.filter(
  (q) => q.parcelSpec === liveParcelSpecId && !q.modeled && q.priceCny != null && q.priceCny >= 10
);

export const liveQuotesByCountry = new Map<string, LiveQuote[]>();
for (const q of liveQuotes) {
  const arr = liveQuotesByCountry.get(q.countryCode) ?? [];
  arr.push(q);
  liveQuotesByCountry.set(q.countryCode, arr);
}

/** Destination countries that have at least one real captured quote. */
export const liveCountryCodes = Array.from(liveQuotesByCountry.keys()).sort(
  (a, b) => (countryByCode.get(a)?.name ?? a).localeCompare(countryByCode.get(b)?.name ?? b)
);

/** Agents that have at least one real captured quote. */
export const liveAgentIds = Array.from(new Set(liveQuotes.map((q) => q.agentId))).sort();

/** All destination country codes that appear in at least one price row (legacy). */
export const supportedCountryCodes = Array.from(
  new Set(dataset.shippingPrices.map((p) => p.countryCode))
).sort();

export function resolveSources(ids: string[]): Source[] {
  return ids.map((id) => dataset.sources[id]).filter(Boolean);
}
